import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEvents as RewardIcon,
  CardGiftcard as GiftIcon,
  Stars as StarsIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';

const categoryOptions = ['Membership', 'Discount', 'Product', 'Service', 'Voucher'];
const statusOptions = ['Active', 'Inactive'];

const AddReward = () => {
  const history = useHistory();
  const location = useLocation();
  const isEdit = location.state?.isEdit || false;
  const rewardData = location.state?.rewardData || null;

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    pointsRequired: '',
    description: '',
    status: 'Active',
    expiryDate: '',
    quantity: '',
    availableQuantity: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (isEdit && rewardData) {
      setFormData({
        name: rewardData.name || '',
        category: rewardData.category || '',
        pointsRequired: rewardData.pointsRequired || '',
        description: rewardData.description || '',
        status: rewardData.status || 'Active',
        expiryDate: rewardData.expiryDate || '',
        quantity: rewardData.quantity || '',
        availableQuantity: rewardData.availableQuantity || rewardData.quantity || '',
      });
    }
  }, [isEdit, rewardData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name?.trim()) e.name = 'Reward name is required';
    if (!formData.category?.trim()) e.category = 'Category is required';
    if (!formData.pointsRequired || formData.pointsRequired <= 0)
      e.pointsRequired = 'Points required must be greater than 0';
    if (!formData.description?.trim()) e.description = 'Description is required';
    if (!formData.expiryDate) e.expiryDate = 'Expiry date is required';
    if (!formData.quantity || formData.quantity <= 0)
      e.quantity = 'Quantity must be greater than 0';
    if (!formData.availableQuantity || formData.availableQuantity < 0)
      e.availableQuantity = 'Available quantity cannot be negative';
    if (formData.availableQuantity > formData.quantity)
      e.availableQuantity = 'Available quantity cannot exceed total quantity';
    return e;
  };

  const handleSubmit = async () => {
    setServerError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const payload = {
      ...formData,
      pointsRequired: parseInt(formData.pointsRequired),
      quantity: parseInt(formData.quantity),
      availableQuantity: parseInt(formData.availableQuantity),
    };

    try {
      setSubmitting(true);
      // TODO: Replace with actual API endpoint
      // const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      // const url = isEdit ? `${BASE_URL}/api/rewards/${rewardData.id}` : `${BASE_URL}/api/rewards`;
      // const method = isEdit ? 'PUT' : 'POST';
      // const res = await fetch(url, {
      //   method,
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(payload),
      // });
      // if (!res.ok) throw new Error('Failed to save reward');

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      alert(isEdit ? 'Reward updated successfully!' : 'Reward added successfully!');
      history.push('/app/reward/reward-list');
    } catch (err) {
      setServerError(err?.message || 'Failed to save reward');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    history.push('/app/reward/reward-list');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: '#fff' }}>
            <ArrowBackIcon />
          </IconButton>
          <RewardIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isEdit ? 'Edit Reward' : 'Add New Reward'}
          </Typography>
        </Box>
      </Paper>

      {/* Form Content */}
      <Paper sx={{ p: 3 }}>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}

        {/* Basic Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reward Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                size="small"
                error={!!errors.name}
                helperText={errors.name}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GiftIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category *"
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.category}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Points Required *"
                name="pointsRequired"
                type="number"
                value={formData.pointsRequired}
                onChange={handleChange}
                size="small"
                error={!!errors.pointsRequired}
                helperText={errors.pointsRequired}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StarsIcon sx={{ fontSize: 18, color: '#f57c00' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                name="description"
                value={formData.description}
                onChange={handleChange}
                size="small"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                required
                placeholder="Describe the reward details..."
              />
            </Grid>
          </Grid>
        </Box>

        {/* Quantity & Expiry Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Quantity & Expiry Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Quantity *"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                size="small"
                error={!!errors.quantity}
                helperText={errors.quantity}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InventoryIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Available Quantity *"
                name="availableQuantity"
                type="number"
                value={formData.availableQuantity}
                onChange={handleChange}
                size="small"
                error={!!errors.availableQuantity}
                helperText={errors.availableQuantity}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InventoryIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date *"
                name="expiryDate"
                type="date"
                value={formData.expiryDate}
                onChange={handleChange}
                size="small"
                error={!!errors.expiryDate}
                helperText={errors.expiryDate}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleBack} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {submitting ? 'Saving...' : isEdit ? 'Update Reward' : 'Save Reward'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddReward;

